import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { requestPasswordReset } from '../auth/api';
import { AuthCard, Button, FieldLabel, FormError, TextInput } from '../components/FormPrimitives';

/**
 * "Forgot password" page — publicly reachable (no auth required).
 *
 * Submits the email to /api/auth/forgot-password and always shows the same
 * success message regardless of whether the address was registered. This is
 * by design — the backend deliberately doesn't differentiate "no such user"
 * from "email sent" so we can't be used as a user-enumeration oracle.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <AuthCard title="Check your email" subtitle="If we have an account for that address, the reset link is on its way.">
        <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.5, margin: '0 0 16px' }}>
          The link expires in <strong>60 minutes</strong> and can only be used once. If you don't see
          the email, check your spam folder — it comes from{' '}
          <strong>TurnKey &lt;hello@transactionrocket.com&gt;</strong>.
        </p>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 20px' }}>
          Didn't get it? Wait a minute, then try again with the same address.
        </p>
        <Link
          to="/login"
          style={{
            display: 'inline-block',
            textDecoration: 'none',
            color: 'var(--brand-primary)',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          ← Back to sign in
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Forgot your password?"
      subtitle="Enter your email and we'll send you a link to set a new one."
    >
      <form onSubmit={onSubmit} noValidate>
        <FormError message={error} />

        <div style={{ marginBottom: 20 }}>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <TextInput
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={submitting}
          />
        </div>

        <Button type="submit" disabled={submitting || !email} style={{ width: '100%', marginBottom: 12 }}>
          {submitting ? 'Sending…' : 'Send reset link'}
        </Button>

        <div style={{ textAlign: 'center' }}>
          <Link
            to="/login"
            style={{
              textDecoration: 'none',
              color: 'var(--brand-primary)',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            ← Back to sign in
          </Link>
        </div>
      </form>
    </AuthCard>
  );
}
